import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Loader } from 'components/Loader/Loader';
import { config } from 'config/config';
import { useUserSearchContext } from 'context/UserSearchContext';
import React from 'react';
import { ExtrasRentalRate, RentalRate } from 'types/BookingDetails';
interface AmountBreakDownProps {
  open: boolean;
  onClose: () => void;
}
const AmountBreakDown: React.FC<AmountBreakDownProps> = ({ open, onClose }) => {
  const { bookingSearchData, loading } = useUserSearchContext();

  const prePaidPrice: RentalRate = bookingSearchData.RentalRate.find(
    (rentalRate: RentalRate) => rentalRate.Code === 'PPD'
  );

  const validRentals =
    bookingSearchData?.RentalRate?.filter((rental: RentalRate) =>
      // bookingSearchData?.chargeCodes?.includes(rental.Code) &&
      ['TAR', 'DIT', 'PPD'].includes(rental.Code)
    ) || [];

  const totalAmount = validRentals.reduce(
    (sum: number, rental: RentalRate) => sum + (rental?.TotalAmt || 0),
    0
  );

  const discount = bookingSearchData?.PromoCodeDis ?? 0;

  const finalAmount =
    discount > 0 ? totalAmount * (1 - discount / 100) : totalAmount;

  // Insurance Total
  const insuranceTotal =
    bookingSearchData?.RentalRate?.reduce((sum: number, rate: RentalRate) => {
      if (
        bookingSearchData.chargeCodes.includes(rate.Code) &&
        config.insuranceChargeCode.includes(rate.Code)
      ) {
        return sum + (rate.TotalAmt || 0);
      }
      return sum;
    }, 0) || 0;

  // Ancillaries Total
  const ancillariesTotal =
    bookingSearchData?.RentalRate?.reduce((sum: number, rate: RentalRate) => {
      if (
        bookingSearchData.chargeCodes.includes(rate.Code) &&
        config.ancilarriesChargeCode.includes(rate.Code)
      ) {
        return sum + (rate.TotalAmt || 0);
      }
      return sum;
    }, 0) || 0;

  // Extras Total
  const extrasTotal =
    bookingSearchData?.ExtrasRentalRate?.reduce(
      (sum: number, extra: ExtrasRentalRate) => {
        if (bookingSearchData.chargeCodes.includes(extra.Code)) {
          return sum + (extra.TotalAmt || 0);
        }
        return sum;
      },
      0
    ) || 0;

  const grandTotal =
    finalAmount + insuranceTotal + ancillariesTotal + extrasTotal;

  if (loading) return <Loader />;

  return (
    bookingSearchData && (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          component={'h6'}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          Bill Details
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="content-section">
          {/* Price Details */}
          <Typography
            className="text-heading"
            fontWeight="bold"
            mt={2}
            gutterBottom
          >
            Booking Details
          </Typography>
          {bookingSearchData.OutDetails && (
            <Box display="flex" justifyContent="space-between" mb={2} mt={2}>
              <Typography className="content-text">Pickup</Typography>
              <Typography
                className="content-text"
                width={'80%'}
                textAlign={'end'}
              >
                {`${bookingSearchData.OutDetails.BranchName}, ${bookingSearchData.OutDetails.StateName}, ${bookingSearchData.OutDetails.Date}, ${bookingSearchData.OutDetails.Time}`}
              </Typography>
            </Box>
          )}
          {bookingSearchData.InDetails && (
            <Box display="flex" justifyContent="space-between" mb={2} mt={2}>
              <Typography className="content-text">Return</Typography>
              <Typography
                className="content-text"
                width={'80%'}
                textAlign={'end'}
              >
                {`${bookingSearchData.InDetails.BranchName}, ${bookingSearchData.InDetails.StateName}, ${bookingSearchData.InDetails.Date}, ${bookingSearchData.InDetails.Time}`}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />
          {/* Price Details */}

          <Typography
            className="text-heading"
            fontWeight="bold"
            mt={2}
            gutterBottom
          >
            Vehicle Details
          </Typography>

          {bookingSearchData.VehicleModel.Name && (
            <Box display="flex" justifyContent="space-between" mb={2} mt={2}>
              <Typography className="content-text">Name</Typography>
              <Typography className="content-text">
                {`${bookingSearchData.VehicleModel.Name} (or similar)`}
              </Typography>
            </Box>
          )}

          {bookingSearchData.VehicleModel.Passengers && (
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography className="content-text">Passengers</Typography>
              <Typography className="content-text">
                {`${bookingSearchData.VehicleModel.Passengers}`}
              </Typography>
            </Box>
          )}

          {bookingSearchData.VehicleModel.Doors && (
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography className="content-text">Doors</Typography>
              <Typography className="content-text">
                {`${bookingSearchData.VehicleModel.Doors}`}
              </Typography>
            </Box>
          )}

          {bookingSearchData.VehicleModel.Engine && (
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography className="content-text">Engine</Typography>
              <Typography className="content-text">
                {`${bookingSearchData.VehicleModel.Engine}`}
              </Typography>
            </Box>
          )}

          {bookingSearchData.VehicleModel.FuelType && (
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography className="content-text">FuelType</Typography>
              <Typography className="content-text">
                {`${bookingSearchData.VehicleModel.FuelType}`}
              </Typography>
            </Box>
          )}

          {bookingSearchData.VehicleModel.AutoTransmission && (
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography className="content-text">
                Gear Transmission
              </Typography>
              <Typography className="content-text">
                {`${bookingSearchData.VehicleModel.AutoTransmission ? 'Automatic' : 'Manual'}`}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Rental */}
          {/* <Typography className="text-heading" fontWeight="bold" gutterBottom>
            Rental
          </Typography> */}
          {validRentals.length > 0 && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography className="content-text">
                Rental Total {discount > 0 ? 'with Discount' : ''}
              </Typography>

              <Typography className="content-text">
                AED {finalAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Insurance */}
          <Typography className="text-heading" fontWeight="bold" gutterBottom>
            Insurance
          </Typography>
          {bookingSearchData.chargeCodes &&
            bookingSearchData.chargeCodes.length > 0 &&
            bookingSearchData.RentalRate.map(
              (insurance: RentalRate, index: number) =>
                bookingSearchData.chargeCodes.includes(insurance.Code) &&
                config.insuranceChargeCode.includes(insurance.Code) && (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography className="content-text">
                      {insurance.Name}
                    </Typography>
                    <Typography className="content-text">
                      AED {insurance.TotalAmt} /{insurance.PaidDuration}{' '}
                      {insurance.PaidDuration > 1 ? 'Days' : 'Day'}
                    </Typography>
                  </Box>
                )
            )}

          <Divider sx={{ mb: 2 }} />
          <Typography fontWeight="bold" gutterBottom>
            Add-ons/Extras
          </Typography>

          {bookingSearchData.chargeCodes &&
            bookingSearchData.chargeCodes.length > 0 &&
            bookingSearchData.RentalRate.map(
              (insurance: RentalRate, index: number) =>
                config.ancilarriesChargeCode.includes(insurance.Code) &&
                bookingSearchData.chargeCodes.includes(insurance.Code) && (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography className="content-text">
                      {insurance.Name}
                    </Typography>
                    <Typography className="content-text">
                      {`AED ${insurance.TotalAmt} / ${insurance.PaidDuration} ${
                        insurance.PaidDuration > 1 ? 'Days' : 'Day'
                      }`}
                    </Typography>
                  </Box>
                )
            )}
          {bookingSearchData &&
            bookingSearchData.ExtrasRentalRate &&
            bookingSearchData.ExtrasRentalRate.map(
              (extrasRentalRate: ExtrasRentalRate, index: number) =>
                bookingSearchData.chargeCodes.includes(
                  extrasRentalRate.Code
                ) && (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography className="content-text">
                      {extrasRentalRate.Name}
                    </Typography>
                    <Typography className="content-text">
                      {`AED ${extrasRentalRate.TotalAmt} / ${bookingSearchData.RentalDays} ${
                        bookingSearchData.RentalDays > 1 ? 'Days' : 'Day'
                      }`}
                    </Typography>
                  </Box>
                )
            )}
        </DialogContent>

        <DialogTitle
          component={'h6'}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          Total
          <span>
            AED {Number(grandTotal.toFixed(2))} / {bookingSearchData.RentalDays}{' '}
            {bookingSearchData.RentalDays > 1 ? 'Days' : 'Day'}
          </span>
        </DialogTitle>
      </Dialog>
    )
  );
};

export default AmountBreakDown;
